import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { useAuth } from "../context/AuthContext";
import { useChatSocket } from "../context/ChatSocketContext";
import { useResponsive } from "../hooks/useResponsive";
import { listAppUsers, type AppUser } from "../services/userService";
import {
  chatService,
  type ChatConversation,
  type ChatConversationDetail,
  type ChatTeamSummary,
} from "../services/chatService";
import { ConversationList } from "../components/chat/ConversationList";
import { ChatThread } from "../components/chat/ChatThread";
import { NewChatDraft } from "../components/chat/NewChatDraft";
import { MiniSpinner } from "../components/chat/chatUi";
import { INK2, LINE, SIDEBAR_BG } from "../components/chat/chatTheme";

const ChatPage: React.FC = () => {
  const { user } = useAuth();
  const currentUserId: string | null = user?.id ?? null;
  const currentUserName: string | null =
    [user?.firstName, user?.lastName].filter(Boolean).join(" ").trim() ||
    user?.name ||
    user?.email ||
    null;
  const navigate = useNavigate();
  const { conversationId } = useParams();
  const { isPhone } = useResponsive();
  const { on } = useChatSocket();

  const [conversations, setConversations] = useState<ChatConversation[]>([]);
  const [teams, setTeams] = useState<ChatTeamSummary[]>([]);
  const [users, setUsers] = useState<AppUser[]>([]);
  const [detail, setDetail] = useState<ChatConversationDetail | null>(null);
  const [loadingList, setLoadingList] = useState(true);

  const activeId = conversationId ?? null;
  // „+": Fenster für eine Nachricht an mehrere (geht als Direktnachricht raus).
  const [neuerChat, setNeuerChat] = useState(false);
  useEffect(() => {
    if (activeId) setNeuerChat(false);
  }, [activeId]);

  const refreshList = useCallback(async () => {
    try {
      const [list, myTeams] = await Promise.all([
        chatService.listConversations(),
        chatService.listMyTeams().catch(() => [] as ChatTeamSummary[]),
      ]);
      setConversations(list);
      setTeams(myTeams);
    } finally {
      setLoadingList(false);
    }
  }, []);

  useEffect(() => {
    listAppUsers().then(setUsers).catch(() => undefined);
    refreshList();
  }, [refreshList]);

  // Aktive Conversation-Details (Mitglieder + Verwaltungsrechte) laden — beim
  // Wechsel der Conversation und nach jeder Mitgliederänderung. Der Zähler
  // verwirft verspätete Antworten eines bereits verlassenen Chats (A→B, A
  // antwortet nach B), statt sie über den aktuellen Stand zu schreiben.
  const detailSeqRef = useRef(0);
  const refreshDetail = useCallback(async () => {
    const seq = ++detailSeqRef.current;
    if (!activeId) {
      setDetail(null);
      return;
    }
    try {
      const d = await chatService.getConversation(activeId);
      if (seq === detailSeqRef.current) setDetail(d);
    } catch {
      if (seq === detailSeqRef.current) setDetail(null);
    }
  }, [activeId]);

  useEffect(() => {
    void refreshDetail();
  }, [refreshDetail]);

  // Neue eingehende Unterhaltung → Liste (inkl. Teams) nachladen.
  useEffect(() => {
    const off = on("unread:update", (p: { conversationId: string }) => {
      if (!conversations.some((c) => c.id === p.conversationId)) {
        refreshList();
      }
    });
    return off;
  }, [on, conversations, refreshList]);

  const sortedConversations = useMemo(() => {
    return conversations.slice().sort((a, b) => {
      const ta = a.lastMessageAt ? Date.parse(a.lastMessageAt) : Date.parse(a.createdAt);
      const tb = b.lastMessageAt ? Date.parse(b.lastMessageAt) : Date.parse(b.createdAt);
      return tb - ta;
    });
  }, [conversations]);

  const selectConversation = (id: string) => navigate(`/chat/${id}`);

  const startDm = async (userId: string) => {
    try {
      const conv = await chatService.getOrCreateDm(userId);
      await refreshList();
      navigate(`/chat/${conv.id}`);
    } catch {
      /* ignorieren */
    }
  };

  const openNewChat = () => {
    setNeuerChat(true);
    navigate("/chat");
  };

  const openTeam = async (teamId: string) => {
    try {
      const conv = await chatService.getOrCreateTeamConversation(teamId);
      await refreshList();
      navigate(`/chat/${conv.id}`);
    } catch {
      /* ignorieren */
    }
  };

  const showList = !isPhone || (!activeId && !neuerChat);
  const showThread = !isPhone || !!activeId || neuerChat;

  return (
    // Wurzelhöhe = Viewport minus Topbar (`--topbar-h`, = 64px + obere Safe-Area),
    // minus Fenster-Dock (--wm-dock-h) UND minus mobile Bottom-Navigation
    // (--mobile-nav-h, Fallback 0) — exakt die drei Abzüge aus .vgd-notescard
    // (vorgangDetail.css). `<main>` reserviert unten `--wm-dock-h` als
    // padding-bottom; fehlt der Abzug hier, ist der Chat um genau diesen Rest zu
    // hoch, `<main>` scrollt und unter dem Rahmen bleibt ein toter Streifen.
    <div className="kvp-rd" style={{ height: "calc(100vh - var(--topbar-h, 64px) - var(--wm-dock-h, 4px) - var(--mobile-nav-h, 0px))", padding: isPhone ? 0 : 12, boxSizing: "border-box", fontFamily: "'Barlow', 'Segoe UI', sans-serif" }}>
      <div
        style={{
          display: "flex",
          height: "100%",
          minHeight: 0,
          border: `1px solid ${LINE}`,
          borderRadius: isPhone ? 0 : 14,
          overflow: "hidden",
          background: "#fff",
        }}
      >
        {showList && (
          <div
            style={{
              width: isPhone ? "100%" : 330,
              flexShrink: 0,
              borderRight: isPhone ? "none" : `1px solid ${LINE}`,
              background: SIDEBAR_BG,
              display: "flex",
              flexDirection: "column",
              minHeight: 0,
            }}
          >
            {loadingList ? (
              <div style={{ padding: 24, textAlign: "center" }}>
                <MiniSpinner label="Lädt…" />
              </div>
            ) : (
              <ConversationList
                conversations={sortedConversations}
                teams={teams}
                users={users}
                currentUserId={currentUserId}
                activeId={activeId}
                onSelect={selectConversation}
                onOpenTeam={openTeam}
                onOpenDmUser={startDm}
                onNewChat={openNewChat}
                newChatOpen={neuerChat}
              />
            )}
          </div>
        )}

        {showThread && (
          <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", minHeight: 0 }}>
            {neuerChat && !activeId ? (
              <NewChatDraft
                users={users}
                currentUserId={currentUserId}
                onBack={isPhone ? () => setNeuerChat(false) : undefined}
                onSent={async (convs) => {
                  await refreshList();
                  // Ein Empfänger → direkt in dessen Unterhaltung; bei mehreren
                  // bleibt das Fenster mit der Bestätigung stehen.
                  if (convs.length === 1) navigate(`/chat/${convs[0].id}`);
                }}
              />
            ) : !activeId ? (
              <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", color: INK2, fontSize: 14 }}>
                Wähle links eine Unterhaltung oder starte eine neue.
              </div>
            ) : !detail ? (
              <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <MiniSpinner label="Lädt…" />
              </div>
            ) : (
              <ChatThread
                key={detail.conversation.id}
                conversation={detail.conversation}
                members={detail.members}
                users={users}
                currentUserId={currentUserId}
                currentUserName={currentUserName}
                onBack={isPhone ? () => navigate("/chat") : undefined}
                canManage={detail.canManage}
                canDelete={detail.canDelete}
                onMembersChanged={() => {
                  void refreshDetail();
                  void refreshList();
                }}
                onGroupDeleted={() => {
                  void refreshList();
                  navigate("/chat");
                }}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatPage;
